import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'
import { GradientText } from '@/design-system'
import { LandingNav } from '../landing-v2/LandingNav'
import { APP_NAME } from '@/utils/constants'
import { LogoMark } from '@/features/album/components/EgowallLogo'

export type LegalSection = { id: string; title: string }

/* ── page shell ──────────────────────────────────────────────────────── */

const Page = styled(Box)({
  background: 'var(--ds-surface)',
  color: 'var(--ds-ink)',
  minHeight: '100vh',
  fontFamily: 'var(--ds-font)',
})

const Hero = styled('header')({
  textAlign: 'center',
  padding: '146px 32px 40px',
  position: 'relative',
  overflow: 'hidden',
})

const HeroGlow = styled(Box)({
  position: 'absolute',
  top: -260,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 1000,
  height: 560,
  background:
    'radial-gradient(ellipse 50% 50% at 50% 50%, rgba(255,207,125,0.13), rgba(190,157,253,0.1) 50%, transparent 75%)',
  pointerEvents: 'none',
})

const Title = styled('h1')({
  fontFamily: 'var(--ds-font)',
  fontSize: 'clamp(32px, 5vw, 52px)',
  fontWeight: 760,
  letterSpacing: '-0.04em',
  lineHeight: 1.05,
  marginTop: 14,
  marginBottom: 0,
  color: 'var(--ds-ink)',
  position: 'relative',
})

const UpdatedLabel = styled('p')({
  fontFamily: 'var(--ds-font-mono, "IBM Plex Mono", monospace)',
  fontSize: 11.5,
  letterSpacing: '0.08em',
  color: 'var(--ds-ink-subtle)',
  margin: '16px 0 0',
  position: 'relative',
})

/* ── two-column body ─────────────────────────────────────────────────── */

const Body = styled(Box)({
  maxWidth: 1140,
  margin: '0 auto',
  padding: '0 32px 100px',
  display: 'grid',
  gridTemplateColumns: '240px 1fr',
  gap: 56,
  alignItems: 'start',
  '@media (max-width: 900px)': { gridTemplateColumns: '1fr', gap: 32, padding: '0 20px 80px' },
})

const TocAside = styled('aside')({
  position: 'sticky',
  top: 90,
  '@media (max-width: 900px)': { position: 'static', order: -1 },
})

const TocCard = styled(Box)({
  background: 'var(--ds-canvas)',
  borderRadius: 'var(--ds-radius-lg)',
  boxShadow: 'var(--ds-shadow-soft)',
  padding: '18px 8px',
  maxHeight: 'calc(100vh - 130px)',
  overflowY: 'auto',
  '@media (max-width: 900px)': { maxHeight: 220 },
})

const TocTitle = styled('h4')({
  fontFamily: 'var(--ds-font-mono, "IBM Plex Mono", monospace)',
  fontSize: 10,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--ds-ink-subtle)',
  fontWeight: 600,
  margin: '4px 14px 10px',
})

const TocList = styled(Box)({ display: 'flex', flexDirection: 'column' })

const TocLink = styled('a', { shouldForwardProp: (p) => p !== '$active' })<{ $active?: boolean }>(
  ({ $active }) => ({
    display: 'block',
    padding: '7px 14px',
    borderRadius: 'var(--ds-radius-md)',
    fontSize: 12.5,
    lineHeight: 1.4,
    textDecoration: 'none',
    color: $active ? 'var(--ds-ink)' : 'var(--ds-ink-subtle)',
    fontWeight: $active ? 640 : 500,
    background: $active ? 'var(--ds-surface)' : 'transparent',
    transition: 'color 0.12s ease, background 0.12s ease',
    '&:hover': { color: 'var(--ds-ink)', background: 'var(--ds-surface)' },
  }),
)

/** Targets bare semantic tags so legal copy can stay close to plain
 * markup instead of every paragraph needing its own styled component. */
const Content = styled(Box)({
  fontSize: 14.5,
  lineHeight: 1.7,
  color: 'var(--ds-ink-muted)',
  '& section': { marginBottom: 44, paddingTop: 4 },
  '& h2': {
    fontSize: 20,
    fontWeight: 720,
    letterSpacing: '-0.01em',
    color: 'var(--ds-ink)',
    marginTop: 0,
    marginBottom: 14,
  },
  '& h3': {
    fontSize: 15,
    fontWeight: 680,
    color: 'var(--ds-ink)',
    marginTop: 22,
    marginBottom: 8,
  },
  '& p': { margin: '0 0 12px' },
  '& ul': { margin: '0 0 12px', paddingLeft: 20 },
  '& li': { margin: '0 0 6px' },
  '& strong': { color: 'var(--ds-ink)', fontWeight: 640 },
  '& a': { color: 'var(--ds-accent-deep-a, #e97a4a)', textDecoration: 'none' },
  '& a:hover': { textDecoration: 'underline' },
})

const ContactCard = styled(Box)({
  background: 'var(--ds-surface)',
  borderRadius: 'var(--ds-radius-md)',
  padding: '16px 18px',
  marginTop: 10,
  '& p': { margin: '0 0 8px' },
  '& p:last-child': { margin: 0 },
})

/* ── footer (shared marketing footer) ────────────────────────────────── */

const Foot = styled('footer')({
  background: 'var(--ds-night-canvas)',
  color: 'var(--ds-night-ink)',
  borderTop: '1px solid var(--ds-night-border)',
})

const FootInner = styled(Box)({
  maxWidth: 1240,
  margin: '0 auto',
  padding: '44px clamp(20px, 4vw, 48px) 30px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 16,
})

const FootBrand = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontWeight: 760,
  color: 'var(--ds-night-ink)',
  fontSize: 14,
})

const FootLinks = styled(Box)({ display: 'flex', gap: 22, flexWrap: 'wrap' })

const FootLink = styled(RouterLink)({
  fontSize: 12.5,
  color: 'var(--ds-night-ink-muted, rgba(244,243,246,0.55))',
  textDecoration: 'none',
  '&:hover': { color: 'var(--ds-night-ink)' },
})

const FootLegal = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.35))',
})

/* ── component ────────────────────────────────────────────────────────── */

interface LegalLayoutProps {
  eyebrow: string
  title: string
  updatedLabel: string
  sections: LegalSection[]
  children: React.ReactNode
}

export function LegalLayout({ eyebrow, title, updatedLabel, sections, children }: LegalLayoutProps) {
  const [activeId, setActiveId] = useState('')

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) setActiveId(visible[0].target.id)
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0 },
    )

    sections.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [sections])

  return (
    <Page>
      <LandingNav />

      <Hero>
        <HeroGlow />
        <GradientText variant='eyebrow'>{eyebrow}</GradientText>
        <Title>{title}</Title>
        <UpdatedLabel>{updatedLabel}</UpdatedLabel>
      </Hero>

      <Body>
        <TocAside>
          <TocCard>
            <TocTitle>On this page</TocTitle>
            <TocList>
              {sections.map((s) => (
                <TocLink key={s.id} href={`#${s.id}`} $active={activeId === s.id}>
                  {s.title}
                </TocLink>
              ))}
            </TocList>
          </TocCard>
        </TocAside>

        <Content>{children}</Content>
      </Body>

      <Foot>
        <FootInner>
          <FootBrand>
            <LogoMark />
            {APP_NAME}
          </FootBrand>
          <FootLinks>
            <FootLink to='/'>Home</FootLink>
            <FootLink to='/pricing'>Pricing</FootLink>
            <FootLink to='/terms'>Terms</FootLink>
            <FootLink to='/privacy'>Privacy</FootLink>
          </FootLinks>
          <FootLegal>© {new Date().getFullYear()} EGOWALL, LLC</FootLegal>
        </FootInner>
      </Foot>
    </Page>
  )
}

export { ContactCard }
